import { makeAutoObservable, observable, reaction, runInAction } from "mobx";
import type { RootStore } from "./root.store";
import { PASSWORD_POLICY_FLAG } from "@/lib/launchdarkly/flags";
import type { TAuditEvent, TAuditEventsPage } from "@/types/audit";

const AUDIT_DEFAULT_PER_PAGE = 50;

// NG-1368/NG-1369's login.succeeded/login.failed event types (audit_seeds.py's
// category "auth"), the two rows this workspace's Audit Log gates on the
// password-policy flag (NG-1610).
const PASSWORD_POLICY_AUDIT_CATEGORY = "auth";

interface PageState {
  results: TAuditEvent[];
  nextCursor: string | null;
  hasNext: boolean;
  totalCount: number;
}

const EMPTY_PAGE_STATE: PageState = {
  results: [],
  nextCursor: null,
  hasNext: false,
  totalCount: 0,
};

function envelopeToPageState(page: TAuditEventsPage): PageState {
  return {
    results: page.results,
    nextCursor: page.next_page_results ? page.next_cursor : null,
    hasNext: page.next_page_results,
    totalCount: page.total_count,
  };
}

/**
 * Read-only, cursor-paginated audit-event list for the Admin Settings →
 * Operations → Audit Log page. The backend returns rows newest-first; this
 * store just walks pages for the DataTable's infinite scroll. No mutations —
 * ``audit_events`` is append-only.
 */
export class AuditEventsStore {
  pageBySlug = observable.map<string, PageState>();
  isLoading = false;
  isLoadingMore = false;
  // Last list-load failure, surfaced to the DataTable `error` prop. Cleared
  // when a (re)load starts; set when loadPage/loadMore reject.
  loadError: Error | null = null;

  private rootStore: RootStore;
  private inflight = new Map<string, Promise<void>>();
  // Set once the deferred `reaction()` below actually registers (see the
  // queueMicrotask comment). `dispose()` is a no-op if called before then, so
  // an owner that replaces this store in the same tick as constructing it
  // disposes nothing and leaves the reaction to register against an orphaned
  // store on its own microtask tick. Harmless (the guard clauses inside it
  // just find nothing to do and it's a single one-time registration, not a
  // leak that compounds), but worth naming rather than presenting this as
  // watertight.
  private disposeReaction: (() => void) | null = null;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(
      this,
      { rootStore: false, inflight: false, disposeReaction: false } as never,
      { autoBind: true },
    );

    // NG-1610: a cold page load can call loadPage() before LaunchDarkly is
    // ready, in which case excludeCategoryFilter treats the flag as off and
    // the first page comes back with the auth-category rows already
    // filtered out (or, symmetrically, an on→off flip could leave them
    // visible). Once the flag's resolved value actually changes, reload the
    // current workspace's list so it matches — loadMore alone can't fix
    // this, since switching filters mid-cursor wouldn't backfill rows
    // already excluded from earlier pages.
    //
    // Deferred to a microtask: RootStore constructs AuditEventsStore before
    // FeatureFlagStore, so reading `this.rootStore.featureFlags` here
    // synchronously would throw and crash RootStore's whole constructor.
    // By the next microtask, RootStore's constructor (and every sibling
    // store it assigns) has finished running.
    queueMicrotask(() => {
      this.disposeReaction = reaction(
        () =>
          this.rootStore.featureFlags.isReady &&
          this.rootStore.featureFlags.isEnabled(PASSWORD_POLICY_FLAG),
        () => {
          const slug = this.rootStore.router.workspaceSlug;
          if (!slug) return;
          // Nothing has ever asked for this slug's audit log — no stale
          // page to correct, so don't fetch one just because the flag moved.
          if (!this.pageBySlug.has(slug) && !this.inflight.has(slug)) return;
          // Wait for any in-flight request to settle before reloading: it
          // may have already read the OLD filter value, so simply letting
          // it finish (without a follow-up reload) would leave a stale
          // page even though the reaction did fire. `.catch` here — not
          // the other fire-and-forget loadPage/loadMore callers' pattern —
          // because loadPage already records the failure on `loadError`;
          // this just stops it from becoming an unhandled rejection.
          const pending = this.inflight.get(slug) ?? Promise.resolve();
          void pending.finally(() => this.loadPage(slug)).catch(() => {});
        },
      );
    });
  }

  /** Stop the flag-change reaction above. Call it before replacing this store
   *  with a fresh one; without it, the old reaction stays permanently
   *  subscribed to the (now-orphaned) store's featureFlags read, so it never
   *  garbage-collects and can still fire loadPage() on a store nothing else
   *  references. */
  dispose(): void {
    this.disposeReaction?.();
    this.disposeReaction = null;
  }

  get events(): TAuditEvent[] {
    const slug = this.rootStore.router.workspaceSlug;
    if (!slug) return [];
    return (this.pageBySlug.get(slug) ?? EMPTY_PAGE_STATE).results;
  }

  get pagination(): PageState {
    const slug = this.rootStore.router.workspaceSlug;
    if (!slug) return EMPTY_PAGE_STATE;
    return this.pageBySlug.get(slug) ?? EMPTY_PAGE_STATE;
  }

  /** NG-1610: while password-policy is off, keep the login.* rows out of the
   *  list and CSV export rather than shipping them unfilterable the moment
   *  NG-1368/1369 land. Public: the audit-log route's CSV export reuses this
   *  exact mapping rather than re-deriving it from the flag, so the two can't
   *  disagree on which rows are excluded. */
  get excludeCategoryFilter(): { exclude_category: string } | Record<string, never> {
    return this.rootStore.featureFlags.isEnabled(PASSWORD_POLICY_FLAG)
      ? {}
      : { exclude_category: PASSWORD_POLICY_AUDIT_CATEGORY };
  }

  async loadPage(slug: string): Promise<void> {
    const existing = this.inflight.get(slug);
    if (existing) return existing;
    const promise = (async () => {
      runInAction(() => {
        this.isLoading = true;
        this.loadError = null;
      });
      try {
        const page = await this.rootStore.auditService.list(slug, {
          per_page: AUDIT_DEFAULT_PER_PAGE,
          ...this.excludeCategoryFilter,
        });
        runInAction(() => {
          this.pageBySlug.set(slug, envelopeToPageState(page));
        });
      } catch (err) {
        runInAction(() => {
          this.loadError = err instanceof Error ? err : new Error(String(err));
        });
        throw err;
      } finally {
        runInAction(() => {
          this.isLoading = false;
        });
      }
    })().finally(() => {
      this.inflight.delete(slug);
    });
    this.inflight.set(slug, promise);
    return promise;
  }

  async loadMore(slug: string): Promise<void> {
    const current = this.pageBySlug.get(slug);
    // No page yet (initial load failed) — the DataTable error-empty "Try again"
    // routes the retry here, so re-run the first page rather than no-opping.
    if (!current) {
      await this.loadPage(slug);
      return;
    }
    if (!current.hasNext || !current.nextCursor || this.isLoadingMore) return;
    runInAction(() => {
      this.isLoadingMore = true;
      this.loadError = null;
    });
    try {
      const page = await this.rootStore.auditService.list(slug, {
        per_page: AUDIT_DEFAULT_PER_PAGE,
        cursor: current.nextCursor,
        ...this.excludeCategoryFilter,
      });
      const next = envelopeToPageState(page);
      runInAction(() => {
        const existing = this.pageBySlug.get(slug);
        const seen = new Set((existing?.results ?? []).map((e) => e.id));
        const merged = [
          ...(existing?.results ?? []),
          ...next.results.filter((e) => !seen.has(e.id)),
        ];
        this.pageBySlug.set(slug, { ...next, results: merged });
      });
    } catch (err) {
      runInAction(() => {
        this.loadError = err instanceof Error ? err : new Error(String(err));
      });
      throw err;
    } finally {
      runInAction(() => {
        this.isLoadingMore = false;
      });
    }
  }
}
